import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, Crown, Receipt, Copy, Check, PackageOpen } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import DiscordLinkButton from '@/components/DiscordLinkButton';
import { useAuth } from '@/context/AuthContext';
import { api, API_BASE } from '@/lib/api';

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [copied, setCopied] = useState(false);
  const [owned, setOwned] = useState({});

  useEffect(() => {
    if (!user) return;
    api.get('/payments/orders').then((r) => setOrders(r.data)).catch(() => {});
    api.get('/products').then((r) => setProducts(r.data)).catch(() => {});
    api.get('/downloads/owned').then((r) => setOwned(r.data)).catch(() => {});
    refresh();
  // eslint-disable-next-line
  }, [user?.id]);

  if (loading) return <div className="text-center pt-40 text-white/50">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const ownedProducts = products.filter((p) => owned[p.id]);

  const copyCode = async () => {
    if (!user.vip_code) return;
    await navigator.clipboard.writeText(user.vip_code);
    setCopied(true); toast.success('VIP code copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const download = async (pid, platform) => {
    try {
      const t = localStorage.getItem('ls_token') || '';
      const res = await fetch(`${API_BASE}/downloads/${pid}${platform ? `?platform=${platform}` : ''}`, {
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error('Download denied');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const cd = res.headers.get('Content-Disposition') || '';
      const nameMatch = cd.match(/filename="?([^"]+)"?/);
      a.href = url;
      a.download = nameMatch ? nameMatch[1] : `${pid}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed — please try again');
    }
  };

  return (
    <div className="bg-grain relative min-h-screen text-white">
      <Navbar />
      <div className="orb" style={{ top: '5%', right: '15%', width: 340, height: 340, background: 'radial-gradient(circle, rgb(var(--brand-rgb)/0.35), transparent 70%)' }} />

      <div className="mx-auto max-w-7xl pt-32 pb-20 px-6 relative space-y-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs uppercase tracking-[0.35em] text-brand font-mono mb-3">Your Dashboard</p>
          <h1 className="font-display font-black text-white text-5xl md:text-6xl tracking-tighter">
            Welcome, <span className="text-brand">{user.name || user.email.split('@')[0]}</span>
          </h1>
        </motion.div>

        {/* VIP card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="glass-strong rounded-2xl p-8"
          data-testid="dashboard-vip-card">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${user.is_vip ? 'bg-brand/20 text-brand' : 'bg-white/5 text-white/40'}`}>
                <Crown className="h-6 w-6" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/50">Membership</div>
                <div className="font-display text-2xl font-semibold">{user.is_vip ? 'Lucky VIP Lifetime' : 'Free Account'}</div>
                {user.is_vip && user.vip_code && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="font-mono text-brand text-sm" data-testid="vip-code-display">{user.vip_code}</span>
                    <button data-testid="copy-vip-code-btn" onClick={copyCode} className="text-white/50 hover:text-white transition-colors">
                      {copied ? <Check className="h-4 w-4 text-brand" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                )}
              </div>
            </div>
            {!user.is_vip && (
              <Link data-testid="dashboard-become-vip" to="/checkout/vip"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-[#0a0a0a] font-semibold hover:shadow-[0_0_30px_rgb(var(--brand-rgb)/0.6)] transition-shadow">
                <Crown className="h-4 w-4" /> Become VIP
              </Link>
            )}
          </div>
          {user.is_vip && <DiscordLinkButton variant="dashboard" />}
        </motion.div>

        {/* Owned products */}
        <section data-testid="owned-products-section">
          <h2 className="font-display font-bold text-2xl mb-6">Your downloads</h2>
          {ownedProducts.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center">
              <PackageOpen className="h-10 w-10 text-white/40 mx-auto" />
              <p className="mt-4 text-white/60">You have not purchased any products yet.</p>
              <Link to="/#products" className="mt-4 inline-block text-brand hover:underline">Browse products →</Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {ownedProducts.map((p) => (
                <div key={p.id} className="glass rounded-2xl p-6" data-testid={`owned-product-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display font-semibold text-white text-lg">{p.name}</div>
                      <div className="text-xs uppercase tracking-widest text-white/40 font-mono">Version {p.version}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-brand">Unlocked</span>
                  </div>
                  <div className="mt-5 flex gap-2">
                    {(p.platforms?.length ? p.platforms.filter((pl) => owned[p.id].includes(pl.id)) : [null]).map((pl) => (
                      <button
                        key={pl?.id || 'file'}
                        data-testid={`download-btn-${p.id}${pl ? `-${pl.id}` : ''}`}
                        onClick={() => download(p.id, pl?.id)}
                        disabled={!p.has_file}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-brand text-[#0a0a0a] font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-[0_0_24px_rgb(var(--brand-rgb)/0.6)] transition-shadow"
                      >
                        <Download className="h-4 w-4" /> {!p.has_file ? 'Not yet available' : pl ? `Download ${pl.label}` : 'Download'}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Purchase history */}
        <section data-testid="purchase-history-section">
          <h2 className="font-display font-bold text-2xl mb-6">Purchase history</h2>
          <div className="glass rounded-2xl overflow-hidden">
            <div className="grid grid-cols-5 gap-4 px-6 py-4 border-b border-white/10 text-xs uppercase tracking-widest text-white/50">
              <div className="col-span-2">Item</div>
              <div>Amount</div>
              <div>Status</div>
              <div>Date</div>
            </div>
            {orders.length === 0 ? (
              <div className="px-6 py-10 text-center text-white/40 text-sm">No purchases yet.</div>
            ) : orders.map((o) => (
              <div key={o.session_id} className="grid grid-cols-5 gap-4 px-6 py-4 border-b border-white/5 items-center text-sm" data-testid={`order-row-${o.session_id}`}>
                <div className="col-span-2 flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-white/40" />
                  <span className="text-white">{o.kind === 'vip' ? 'Lucky VIP Lifetime' : (o.product_name || o.product_id)}{o.platform ? ` · ${o.platform.toUpperCase()}` : ''}</span>
                </div>
                <div className="text-white/70 font-mono">${((o.amount_cents || 0) / 100).toFixed(2)} {String(o.currency || 'USD').toUpperCase()}</div>
                <div className={o.payment_status === 'paid' ? 'text-brand' : 'text-white/50'}>{o.payment_status}</div>
                <div className="text-white/50 text-xs">{new Date(o.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
